import { useEffect } from 'react';
import { ConditionsState, resetState as resetConditionsState } from '@alltrails/conditions/redux/conditionsSlice';
import type { ExploreServiceState } from '@alltrails/explore-service/redux/exploreServiceSlice';
import { resetState as resetExploreServiceState } from '@alltrails/explore-service/redux/exploreServiceSlice';
import { CustomRouteState, resetState as resetCustomRouteState } from '@alltrails/maps/redux/customRoute';
import { type MapState, type PersistedMapKeys, resetState as resetMapState } from '@alltrails/maps/redux/reducer';
import { PhotoTourState, resetState as resetPhotoTourState } from '@alltrails/photo-tour';
import { useAppDispatch } from '@/utils/redux';
import { ExploreCustomRouteState, resetState as resetExploreCustomRouteState } from '@/utils/slices/customRouteSlice';
import { ExploreState, resetState as resetExploreState } from '@/utils/slices/exploreSlice';
import { ExplorePOIState, resetState as resetExplorePOIState } from '@/utils/slices/poiSlice';

/**
 * A hook that resets the state of the explore slice. Specific keys to the explore, explore service, and maps slice can be passed in to persist.
 * This is intended to be used on each page/route level component to ensure state is reset when navigating between parallel routes.
 *
 * @param {Object} options - The options object.
 * @param {Object} options.persistKeys - The memoized keys of the state that should persist when navigating between routes.
 * @param {Array<keyof ConditionsState>} options.persistKeys.conditions - Keys of the state in the conditions slice to persist.
 * @param {Array<keyof CustomRouteState>} options.persistKeys.customRoute - Keys of the state in the custom route slice to persist.
 * @param {Array<keyof ExploreCustomRouteState>} options.persistKeys.exploreCustomRoute - Keys of the state in the explore custom route slice to persist.
 * @param {Array<keyof ExploreState>} options.persistKeys.explore - Keys of the state in the explore slice to persist.
 * @param {Array<keyof ExplorePOIState>} options.persistKeys.explorePOI - Keys of the state in the POI slice to persist.
 * @param {Array<keyof ExploreServiceState>} options.persistKeys.exploreService - Keys of the state in the explore service slice to persist.
 * @param {Array<keyof MapState>} options.persistKeys.maps - Keys of the state in the maps slice to persist.
 * @param {Array<keyof PhotoTourState>} options.persistKeys.photoTour - Keys of the state in the photo tour slice to persist.
 * @param {Object} options.initialState - The memoized partial initial state to apply to the default initial state when resetting the state.
 * @param {Partial<ConditionsState>} options.initialState.conditions - Partial initial state to apply to the default initial state when resetting the conditions slice.
 * @param {Partial<CustomRouteState>} options.initialState.customRoute - Partial initial state to apply to the default initial state when resetting the custom route slice.
 * @param {Partial<ExploreCustomRouteState>} options.initialState.exploreCustomRoute - Partial initial state to apply to the default initial state when resetting the explore custom route slice.
 * @param {Partial<ExploreState>} options.initialState.explore - Partial initial state to apply to the default initial state when resetting the explore slice.
 * @param {Partial<ExplorePOIState>} options.initialState.explorePOI - Partial initial state to apply to the default initial state when resetting the POI slice.
 * @param {Partial<ExploreServiceState>} options.initialState.exploreService - Partial initial state to apply to the default initial state when resetting the explore service slice.
 * @param {Partial<MapState>} options.initialState.maps - Partial initial state to apply to the default initial state when resetting the maps slice.
 * @param {Partial<PhotoTourState>} options.initialState.photoTour - Partial initial state to apply to the default initial state when resetting the photo tour slice.
 */
export function useResetState({
  persistKeys: {
    conditions: persistConditionsKeys,
    customRoute: persistCustomRouteKeys,
    exploreCustomRoute: persistExploreCustomRouteKeys,
    explore: persistExploreKeys,
    explorePOI: persistPOIKeys,
    exploreService: persistExploreServiceKeys,
    maps: persistMapKeys,
    photoTour: persistPhotoTourKeys
  } = {},
  initialState: {
    conditions: initialConditionsState,
    customRoute: initialCustomRouteState,
    exploreCustomRoute: initialExploreCustomRouteState,
    explore: initialExploreState,
    explorePOI: initialPOIState,
    exploreService: initialExploreServiceState,
    maps: initialMapState,
    photoTour: initialPhotoTourState
  } = {}
}: {
  persistKeys?: {
    conditions?: (keyof ConditionsState)[];
    customRoute?: (keyof CustomRouteState)[];
    exploreCustomRoute?: (keyof ExploreCustomRouteState)[];
    explore?: (keyof ExploreState)[];
    explorePOI?: (keyof ExplorePOIState)[];
    exploreService?: (keyof ExploreServiceState)[];
    maps?: (keyof Omit<MapState, PersistedMapKeys>)[];
    photoTour?: (keyof PhotoTourState)[];
  };
  initialState?: {
    conditions?: Partial<ConditionsState>;
    customRoute?: Partial<CustomRouteState>;
    exploreCustomRoute?: Partial<ExploreCustomRouteState>;
    explore?: Partial<ExploreState>;
    explorePOI?: Partial<ExplorePOIState>;
    exploreService?: Partial<ExploreServiceState>;
    maps?: Partial<MapState>;
    photoTour?: Partial<PhotoTourState>;
  };
} = {}) {
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(resetConditionsState({ persistKeys: persistConditionsKeys ?? [], initialState: initialConditionsState }));
    dispatch(resetCustomRouteState({ persistKeys: persistCustomRouteKeys ?? [], initialState: initialCustomRouteState }));
    dispatch(resetExploreCustomRouteState({ persistKeys: persistExploreCustomRouteKeys ?? [], initialState: initialExploreCustomRouteState }));
    dispatch(resetExploreState({ persistKeys: persistExploreKeys ?? [], initialState: initialExploreState }));
    dispatch(resetExplorePOIState({ persistKeys: persistPOIKeys ?? [], initialState: initialPOIState }));
    dispatch(resetExploreServiceState({ persistKeys: persistExploreServiceKeys ?? [], initialState: initialExploreServiceState }));
    dispatch(resetMapState({ persistKeys: persistMapKeys ?? [], initialState: initialMapState }));
    dispatch(resetPhotoTourState({ persistKeys: persistPhotoTourKeys ?? [], initialState: initialPhotoTourState }));
  }, [
    dispatch,
    persistExploreKeys,
    persistCustomRouteKeys,
    persistExploreCustomRouteKeys,
    persistExploreServiceKeys,
    persistMapKeys,
    persistPOIKeys,
    persistPhotoTourKeys,
    initialExploreState,
    initialCustomRouteState,
    initialExploreCustomRouteState,
    initialExploreServiceState,
    initialMapState,
    initialPOIState,
    persistConditionsKeys,
    initialConditionsState,
    initialPhotoTourState
  ]);
}
